import * as _ from 'lodash';
import React, { FormEvent, useEffect, useState } from 'react';
import { ICenterAddressContract } from '@nkr/models/index';
import { Alert, Button, ControlLabel, Form, FormControl, FormGroup, Modal } from 'react-bootstrap';
import { IAddressDataValidation, initialValidation } from './address-data';
import { CenterAddressType } from '@nkr/enums/center-address-type';
import useCenterAddressLookupData from '@nkr/store/center/hooks/use-center-address-lookup-data';
import useCenterAddressById from '@nkr/store/center/hooks/use-center-address-by-id';
import styled from 'styled-components';

const FormWrapper = styled(Form)`
    .form-group {
        margin-bottom: 10px;
    }
`;

interface IProps {
    show: boolean,
    centerId: number,
    addressId: number,
    allowedAddressTypes: CenterAddressType[],
    onUpdated: () => void,
    onCancel: () => void
}

const CenterAddressAddEdit = (props: IProps) => {
    const { addressTypes, states } = useCenterAddressLookupData();
    const { loading, error, address, fetchData, update } = useCenterAddressById();

    const [addressType, setAddressType] = useState(0);
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [addressLine3, setAddressLine3] = useState('');
    const [city, setCity] = useState('');
    const [stateId, setStateId] = useState(-1);
    const [postalCode, setPostalCode] = useState('');
    const [label, setLabel] = useState('');
    const [note, setNote] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [shipToPhoneNumber, setShipToPhoneNumber] = useState('');

    const [validation, setValidation] = useState<IAddressDataValidation>({ ...initialValidation });

    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (props.show == false) {
            setValidation({ ...initialValidation });
        }

        if (props.addressId > 0) {
            fetchData(props.centerId, props.addressId);
        } else {
            setAddressType(props.allowedAddressTypes.length == 1 ? props.allowedAddressTypes[0] : 0);
            setAddressLine1('');
            setAddressLine2('');
            setAddressLine3('');
            setCity('');
            setStateId(-1);
            setPostalCode('');
            setLabel('');
            setNote('');
            setFirstName('');
            setLastName('');
            setShipToPhoneNumber('');
        }
    }, [props.show, props.addressId]);

    useEffect(() => {
        if (address != null) {
            setAddressType(address.typeId);
            setAddressLine1(address.addressLine1);
            setAddressLine2(address.addressLine2);
            setAddressLine3(address.addressLine3);
            setCity(address.city);
            setStateId(address.stateId);
            setPostalCode(address.postalCode);
            setLabel(address.label);
            setNote(address.note);
            setFirstName(address.firstName);
            setLastName(address.lastName);
            setShipToPhoneNumber(address.shipToPhoneNumber);
        }
    }, [address]);

    const handleChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;

        switch (name) {
            case 'type':
                setAddressType(parseInt(value));

                if (parseInt(value) != CenterAddressType.StrongBoxShipTo) {
                    setFirstName('');
                    setLastName('');
                    setShipToPhoneNumber('');
                }

                if (parseInt(value) == CenterAddressType.StrongBoxShipTo) {
                    setAddressLine3('');
                }

                break;  

            case 'addressLine1':
                setAddressLine1(value);
                break;

            case 'addressLine2':
                setAddressLine2(value);
                break;

            case 'addressLine3':
                setAddressLine3(value);
                break;

            case 'city':
                setCity(value);
                break;

            case 'stateId':
                setStateId(parseInt(value));
                break;

            case 'postalCode':
                setPostalCode(value);
                break;

            case 'note':
                setNote(value);
                break;

            case 'label':
                setLabel(value);
                break;

            case 'firstName':
                setFirstName(value);
                break;

            case 'lastName':
                setLastName(value);
                break;

            case 'shipToPhoneNumber':
                setShipToPhoneNumber(value);
                break;
        }
    };

    const handleSubmit = async () => {
        let newValidation: IAddressDataValidation = {
            addressType: addressType > 0,
            addressLine1: addressLine1 != '',
            city: city != '',
            stateId: stateId > 0,
            postalCode: postalCode != '',
            label: addressType == CenterAddressType.StrongBoxShipTo ? label != '' : true,
            firstName: addressType == CenterAddressType.StrongBoxShipTo ? firstName != '' : true,
            lastName: addressType == CenterAddressType.StrongBoxShipTo ? lastName != '' : true,
            shipToPhoneNumber: addressType == CenterAddressType.StrongBoxShipTo ? shipToPhoneNumber != '' : true,
        };

        let isValid =
            newValidation.addressType
            && newValidation.addressLine1
            && newValidation.city
            && newValidation.stateId
            && newValidation.postalCode
            && newValidation.label
            && newValidation.firstName
            && newValidation.lastName
            && newValidation.shipToPhoneNumber;

        setValidation(newValidation);
        setDirty(true);

        if (isValid) {
            let id = address ? address.id : 0;

            let request: ICenterAddressContract = {
                id,
                centerId: props.centerId,
                typeId: addressType,
                addressLine1, addressLine2, addressLine3,
                city, stateId, postalCode,
                note,
                label,
                firstName,
                lastName,
                shipToPhoneNumber
            };

            await update(request);

            props.onUpdated();
        }
    };

    const title = props.addressId == 0 ? 'Add Center Address' : 'Edit Center Address';
    const saveButtonTitle = props.addressId == 0 ? 'Add Center Address' : 'Save Center Address';

    const isValid =
        validation.addressType
        && validation.addressLine1
        && validation.city
        && validation.stateId
        && validation.postalCode
        && validation.label
        && validation.firstName
        && validation.lastName
        && validation.shipToPhoneNumber;

    const addressTypeList = addressTypes.filter(x => props.allowedAddressTypes.length == 0 ? true : props.allowedAddressTypes.indexOf(x.id) >= 0);

    return (
        <>
            <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {!isValid && (
                        <Alert bsStyle='danger' bsSize='sm'>
                            The items in red are required to continue...
                        </Alert>
                    )}

                    <FormWrapper>
                        <FormGroup validationState={dirty && validation.addressLine1 == false ? 'error' : null}>
                            <ControlLabel>Address Type</ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='type'
                                disabled={props.allowedAddressTypes.length == 1}
                                value={addressType}
                                onChange={handleChange}
                            >
                                <option value={0}>- Select -</option>
                                {addressTypeList.map(item =>
                                    <option key={'type-' + item.id} value={item.id}>{item.description}</option>
                                )}
                            </FormControl>
                        </FormGroup>

                        <FormGroup validationState={dirty && validation.label == false ? 'error' : null}>
                            <ControlLabel>{addressType != CenterAddressType.StrongBoxShipTo ? 'Name' : 'Company Name'}</ControlLabel>
                            <FormControl
                                name='label'
                                value={label}
                                onChange={handleChange}
                                maxLength={32}
                            />
                        </FormGroup>

                        {addressType != CenterAddressType.StrongBoxShipTo ?
                            <FormGroup>
                                <ControlLabel>Attention</ControlLabel>
                                <FormControl
                                    name='addressLine3'
                                    value={addressLine3}
                                    onChange={handleChange}
                                    maxLength={60}
                                />
                            </FormGroup>
                            :
                            <>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ width: '50%', paddingRight: 5 }}>
                                        <FormGroup validationState={dirty && validation.firstName == false ? 'error' : null}>
                                            <ControlLabel>First Name</ControlLabel>
                                            <FormControl
                                                name='firstName'
                                                value={firstName}
                                                onChange={handleChange}
                                                maxLength={60}
                                            />
                                        </FormGroup>
                                    </div>

                                    <div style={{ width: '50%' }}>
                                        <FormGroup validationState={dirty && validation.lastName == false ? 'error' : null}>
                                            <ControlLabel>Last Name</ControlLabel>
                                            <FormControl
                                                name='lastName'
                                                value={lastName}
                                                onChange={handleChange}
                                                maxLength={60}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>

                                <FormGroup validationState={dirty && validation.shipToPhoneNumber == false ? 'error' : null}>
                                    <ControlLabel>Ship To Phone Number</ControlLabel>
                                    <FormControl
                                        name='shipToPhoneNumber'
                                        value={shipToPhoneNumber}
                                        onChange={handleChange}
                                        maxLength={60}
                                    />
                                </FormGroup>
                            </>
                        }

                        <FormGroup validationState={dirty && validation.addressLine1 == false ? 'error' : null}>
                            <ControlLabel>Street Address</ControlLabel>
                            <FormControl
                                name='addressLine1'
                                value={addressLine1}
                                onChange={handleChange}
                                maxLength={64}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>Suite #/PO Box</ControlLabel>
                            <FormControl
                                name='addressLine2'
                                value={addressLine2}
                                onChange={handleChange}
                                maxLength={20}
                            />
                        </FormGroup>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '45%', paddingRight: 5 }}>
                                <FormGroup validationState={dirty && validation.city == false ? 'error' : null}>
                                    <ControlLabel>City</ControlLabel>
                                    <FormControl
                                        name='city'
                                        value={city}
                                        onChange={handleChange}
                                        maxLength={32}
                                    />
                                </FormGroup>
                            </div>

                            <div style={{ width: '35%', paddingRight: 5 }}>
                                <FormGroup validationState={dirty && validation.stateId == false ? 'error' : null}>
                                    <ControlLabel>State</ControlLabel>
                                    <FormControl
                                        componentClass='select'
                                        name='stateId'
                                        value={stateId}
                                        onChange={handleChange}
                                    >
                                        <option value={0}>- Select -</option>
                                        {states.map(item =>
                                            <option key={'st-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </FormGroup>
                            </div>

                            <div style={{ width: '20%' }}>
                                <FormGroup validationState={dirty && validation.postalCode == false ? 'error' : null}>
                                    <ControlLabel>Postal Code</ControlLabel>
                                    <FormControl
                                        name='postalCode'
                                        value={postalCode}
                                        onChange={handleChange}
                                        maxLength={10}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        <FormGroup>
                            <ControlLabel>Note</ControlLabel>
                            <FormControl
                                componentClass='textarea'
                                rows={3}
                                name='note'
                                style={{ resize: 'none' }}
                                value={note}
                                onChange={handleChange}
                                maxLength={200}
                            />
                        </FormGroup>
                    </FormWrapper>
                </Modal.Body>

                <Modal.Footer>
                    <Button bsStyle='primary' onClick={handleSubmit}>{saveButtonTitle}</Button>
                    <Button onClick={props.onCancel}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CenterAddressAddEdit;